/**
 * HelpHintService
 *
 * Tracks per-card-type "Learn more about X" affordance state for inline chat
 * cards (insights, practices). State is seeded from /auth/me on
 * bootstrap, then mutated locally on each exposure / dismissal with a
 * fire-and-forget POST that keeps the server in sync.
 *
 * Card renderers call `shouldShow(type)` synchronously to decide whether to
 * append the link; immediately after rendering they call `recordExposure(type)`
 * so the next card can see the bumped counter.
 */

import { API_URL } from '../core/api.js';

export const HELP_HINT_TYPES = ['practices', 'insights', 'notes'];
export const EXPOSURE_CAP = 5;

export class HelpHintService {
  constructor() {
    this._state = this._defaultState();
  }

  _defaultState() {
    const out = {};
    for (const t of HELP_HINT_TYPES) out[t] = { exposures: 0, dismissedAt: null };
    return out;
  }

  /**
   * Seed state from the /auth/me bootstrap payload.
   * @param {{state?: object}} helpHints - `user.helpHints` from /auth/me
   */
  hydrate(helpHints) {
    const raw = helpHints && typeof helpHints === 'object' ? helpHints.state : null;
    if (!raw || typeof raw !== 'object') {
      this._state = this._defaultState();
      return;
    }
    const next = this._defaultState();
    for (const t of HELP_HINT_TYPES) {
      const entry = raw[t];
      if (entry && typeof entry === 'object') {
        next[t] = {
          exposures: Number.isInteger(entry.exposures) ? entry.exposures : 0,
          dismissedAt: typeof entry.dismissedAt === 'string' ? entry.dismissedAt : null
        };
      }
    }
    this._state = next;
  }

  /**
   * Drop back to default (nothing seen, nothing dismissed).
   *
   * Call on logout — the app tears down and rebuilds in place without a page
   * reload, so without this one account's dismissals carry into the next
   * account's session on a shared device.
   */
  reset() {
    this._state = this._defaultState();
  }

  /**
   * Should the hint link be rendered for this card type?
   * @param {string} type
   * @returns {boolean}
   */
  shouldShow(type) {
    const entry = this._state[type];
    if (!entry) return false;
    return entry.dismissedAt === null && entry.exposures < EXPOSURE_CAP;
  }

  /**
   * Locally bump the exposure counter and fire-and-forget POST to the server.
   * Call this right after rendering an eligible card.
   * @param {string} type
   */
  recordExposure(type) {
    const entry = this._state[type];
    if (!entry || entry.dismissedAt !== null || entry.exposures >= EXPOSURE_CAP) return;
    entry.exposures += 1;
    this._post(`/help-hints/${type}/exposure`);
  }

  /**
   * Locally mark dismissed and fire-and-forget POST.
   * @param {string} type
   */
  markDismissed(type) {
    const entry = this._state[type];
    if (!entry || entry.dismissedAt !== null) return;
    entry.dismissedAt = new Date().toISOString();
    this._post(`/help-hints/${type}/dismiss`);
  }

  _post(path) {
    try {
      fetch(`${API_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }).catch(err => console.warn('[HelpHintService] sync failed:', path, err));
    } catch (err) {
      console.warn('[HelpHintService] sync threw:', path, err);
    }
  }
}

export const helpHintService = new HelpHintService();

// THE single assignment point for the global. Card renderers read
// `window.helpHintService` synchronously at render time (structuredBlockRenderers
// bails and renders no hint when it is missing), and the app has five ways to
// arrive at a signed-in session — page load authenticated, page load as a
// returning guest, fresh guest via /try-free, in-page sign-in, and partner-invite
// consent. Assigning per-route meant each new route had to remember, and two of
// them didn't. Bootstrap paths now only decide what state to hydrate.
if (typeof window !== 'undefined') {
  window.helpHintService = helpHintService;
}
